import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { VersionToast } from './VersionToast';

const DEFAULT_POLL_MS = 5 * 60 * 1000; // 5 minutes

async function fetchServerVersion(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!response.ok) return null;

    const data = (await response.json()) as { version?: string };
    const remoteVersion = data.version;

    if (
      remoteVersion == null ||
      typeof remoteVersion !== 'string' ||
      remoteVersion.trim() === ''
    ) {
      return null;
    }

    return remoteVersion.trim();
  } catch {
    return null;
  }
}
/**
 * Polls `/version.json` (served in dev by Vite, emitted at build in `dist/`) and shows a Toast
 * when the server version differs from this tab’s bundled `__APP_BUILD_VERSION__`.
 * Skips the initial fetch on reload; the first check runs after the same interval as polling (5 minutes), then repeats.
 */
export const AppVersionUpdateListener = () => {
  const bundledVersion = __APP_BUILD_VERSION__;
  const hasNotifiedRef = useRef(false);

  useEffect(() => {
    const run = async () => {
      const serverVersion = await fetchServerVersion();
      if (serverVersion == null) return;
      if (serverVersion === bundledVersion) return;
      if (hasNotifiedRef.current) return;
      hasNotifiedRef.current = true;

      toast.info((props) => <VersionToast {...props} />, {
        toastId: 'app-version-update-listener-toast',
        autoClose: false,
        hideProgressBar: true,
        closeButton: false,
        closeOnClick: false,
        position: 'top-center',
        style: {
          width: '355px',
        },
      });
    };

    let intervalId: number | undefined;

    /**
     * Set a timeout to run the initial check after the default poll interval (5 minutes), then set an interval to poll every 5 minutes.
     */
    const timeoutId = window.setTimeout(() => {
      void run();
      intervalId = window.setInterval(() => void run(), DEFAULT_POLL_MS);
    }, DEFAULT_POLL_MS);

    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [bundledVersion]);

  return null;
};
